import { useTranslation } from 'react-i18next';
import { FileText, Download, Trash2 } from 'lucide-react';
import { Button } from '~/components/ui';
import { DocumentTypeTag } from '~/components/cards/DocumentTypeTag';
import { formatFileSize, type OrderDocumentType } from '~/utils/documentType';
import { formatDateTime } from '~/utils';

interface DocumentFileRowProps {
  file: OrderDocumentType;
  onDownload: () => void;
  onDelete?: () => void;
  isDownloading?: boolean;
  isDeleting?: boolean;
}

export function DocumentFileRow({
  file,
  onDownload,
  onDelete,
  isDownloading = false,
  isDeleting = false,
}: Readonly<DocumentFileRowProps>) {
  const { t } = useTranslation('order');

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-neut-200 p-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-prim-50 text-prim-700">
          <FileText size={18} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-neut-prim font-medium truncate">
            {file.originalFilename}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <DocumentTypeTag type={file.documentType} />
            <span className="text-xs text-neut-sec">
              {formatFileSize(file.fileSizeBytes)}
            </span>
            <span className="rounded-full px-2.5 py-1 text-xs font-medium bg-prim-100 text-prim-700">
              {t(`order.documents.upload_types.${file.uploadType}`)}
            </span>
            <span className="text-xs text-neut-sec">
              {formatDateTime(file.createdAt)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="accent"
          size="sm"
          width="auto"
          onClick={onDownload}
          loading={isDownloading}
          disabled={isDeleting}
        >
          <Download size={15} aria-hidden="true" />
          {t('order.documents.file_row.download')}
        </Button>
        {onDelete && (
          <Button
            variant="destructive-outline"
            size="sm"
            width="auto"
            onClick={onDelete}
            loading={isDeleting}
            disabled={isDownloading}
          >
            <Trash2 size={15} aria-hidden="true" />
            {t('order.documents.file_row.delete')}
          </Button>
        )}
      </div>
    </div>
  );
}
